import { formatDateNumber } from '@seen/core/dates'

/* Structural subset of a condition's most-recent note (a CommentCommunication),
   so buildConditionNoteHtml stays unit-testable with plain objects. */
export interface ConditionRecentNote {
  message: string
  sent: string
}

export interface ConditionWithRecentNote {
  referenceString: string
  title: string
  recentNote: ConditionRecentNote | undefined
}

export function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/* Decides LAYOUT only, never format. A note whose message contains block-level
   markup can't be nested inside the compact `<p>` wrapper: the parser
   auto-closes it, orphaning a dangling "date · " paragraph above the content
   and appending an empty one below. Such notes get the date on its own line
   instead. Inline markup (<br>, <strong>, mention anchors) nests fine. */
const BLOCK_MARKUP = /<\/?(?:p|div|ul|ol|li|h[1-6]|blockquote|table|pre)\b/i

/* Builds the HTML for the note rendered below a condition. Shared by both
   "@ insert condition + most recent note" commands — single-insert in
   MentionNode and multi-insert in InsertCurrentConditionsWithNotesButtonNode —
   so the two flows can't drift.

   CommentCommunication.message is an HTML field, so it is inserted verbatim.
   Escaping it here would make this the only renderer in Orbit that treats the
   field as plain text — it turned a real <br> between two dated lines into four
   literal characters stored in the note body. escapeHtml stays only where the
   input really is plain text being placed into HTML: the formatted date, and
   the mention's reference and title.

   Missing notes fall back to a "No linked note" placeholder; we never skip the
   note block. */
export function buildConditionNoteHtml(
  recentNote: ConditionRecentNote | undefined
): string {
  if (!recentNote?.message) {
    return '<p><em>No linked note</em></p>'
  }
  const formattedDate = recentNote.sent
    ? escapeHtml(formatDateNumber(recentNote.sent))
    : null
  return BLOCK_MARKUP.test(recentNote.message)
    ? `${formattedDate ? `<p><em>${formattedDate}</em></p>` : ''}${recentNote.message}`
    : `<p>${formattedDate ? `<em>${formattedDate}</em> · ` : ''}${recentNote.message}</p>`
}

/* Bullet list where each condition is a mention followed by its most recent
   note as a nested sub-bullet — the same structure the single-insert command
   produces for one condition while inside a bullet list. */
export function buildConditionsWithNotesListHtml(
  conditions: ConditionWithRecentNote[]
): string {
  const items = conditions
    .map(({ referenceString, title, recentNote }) => {
      const mentionHtml = `<a data-type="mention" data-reference="${escapeHtml(referenceString)}">@${escapeHtml(title)}</a>`
      const noteHtml = buildConditionNoteHtml(recentNote)
      return `<li><p>${mentionHtml}</p><ul><li>${noteHtml}</li></ul></li>`
    })
    .join('')
  return `<ul>${items}</ul>`
}
